<template>
  <div
    v-if="customIsVisible"
    :class="[backgroundColor, bottomMargin, verticalPadding, background]"
    class="text-with-image"
  >
    <div class="mx-auto w-full max-w-screen-2xl px-5 md:px-12">
      <div
        class="relative flex flex-col lg:flex-row lg:gap-x-16"
        :class="{ 'justify-end': textAsLayover && imagePosition === 'right', 'items-center': !textAsLayover }"
      >
        <!-- IMAGE -->
        <div
          class="order-1 rounded-t-lg lg:rounded-lg"
          :class="[
            imagePosition === 'right' ? 'lg:order-2' : '',
            textAsLayover
              ? 'lg:top-0 lg:z-[1] w-full lg:mb-20 lg:h-[630px] lg:max-w-[75%]'
              : 'lg:w-[50%] lg:h-[450px] flex items-center justify-center',
            !isMobile ? 'bg-cover bg-center' : '',
          ]"
          :style="!isMobile ? { backgroundImage: `url(${image})` } : {}"
          :title="!isMobile ? imageDescription : ''"
        >
          <VcImage
            v-if="isMobile"
            :src="image"
            :alt="imageDescription"
            lazy
            class="rounded-t-lg lg:rounded-lg"
            :class="[textAsLayover ? 'lg:h-[630px] w-full' : 'lg:max-h-[450px] rounded-lg']"
          />
        </div>

        <!-- CONTENT -->
        <div
          class="order-2 rounded-b-lg lg:rounded-lg text-block"
          :class="{
            'px-4 lg:px-10 lg:py-5 bg-[--color-body-bg]': textAsLayover,
            'lg:w-[50%]': !textAsLayover,
            'lg:order-1': imagePosition === 'right',
            'lg:absolute z-[2] lg:w-[40%] lg:right-0 lg:bottom-0': textAsLayover,
            'lg:left-0': textAsLayover && imagePosition === 'right',
          }"
        >
          <div v-if="subtitle" class="mt-4 text-lg font-normal">{{ subtitle }}</div>

          <VcTypography :tag="heading" :anchor-id="title" class="mt-4">
            {{ title }}
          </VcTypography>

          <div class="mt-4 text-lg block-content" v-html-safe="contentValue"></div>

          <div v-if="buttons?.length" class="my-4 flex space-x-4">
            <InnoButton
              v-for="(item, index) in buttons"
              :key="index"
              v-bind="getLinkAttr(item.link)"
              class="flex-1 lg:flex-none"
              color="primary"
              :target="'externalLink' in getLinkAttr(item.link) ? '_blank' : '_self'"
              :variant="buttons.length > 1 && index === 0 ? 'solid' : 'outline'"
              shape="pill"
              size="lg"
            >
              {{ item.label }}
            </InnoButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { getLinkAttr } from "@/core/utilities/common";
import { useBreakpoints, breakpointsTailwind } from "@vueuse/core";
import InnoButton from "@/ui-kit/components/molecules/innovadis/inno-button.vue";

type ImagePositionType = "left" | "right";
type ButtonType = {
  label: string;
  link: string;
};

interface IProps {
  heading?: string;
  title: string;
  subtitle?: string;
  text: string | { html: string };
  textAsLayover?: boolean;
  image: string;
  imageDescription: string;
  imagePosition?: ImagePositionType;
  buttons?: Array<ButtonType>;

  id: string;
  background?: string;
  model: any;
  settings: any;

  customIsVisible: boolean;

  backgroundColor: string;
  bottomMargin: string;
  verticalPadding: string;
}

const props = withDefaults(defineProps<IProps>(), {
  heading: "h2",
  textAsLayover: false,
  imagePosition: "left",
});

const contentValue = computed(() => {
  return typeof props.text === "object" ? props.text?.html : props.text;
});

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller("lg");
</script>
